import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
    decrementBundleSelectionQty,
    decrementItemSelectionQty,
    incrementBundleSelectionQty,
    incrementItemSelectionQty,
} from 'api/jalapeno';
import { SELECTIONS_QUERY_KEY } from 'api/queries/useSelectionsQuery';
import { useCarouselContext } from 'contexts/CarouselProvider';
import useGlobalStore from 'hooks/useGlobalStore';
import useProductSource from 'hooks/useProductSource';
import { trackEvent } from 'utils/analyticsUtils';
import logError from 'utils/errorUtils';
import { getOrderSessionStorage, setOrderSessionStorage } from 'utils/orderSessionStorageUtils';
import { updateSelectionsWithOperation } from 'utils/orderUtils';

import { useProductCardContext } from './ProductCardProvider';

/**
 * Enum for mutation operations.
 * @readonly
 * @enum {string}
 */
const OperationTypes = Object.freeze({
    DECREMENT: 'decrement',
    INCREMENT: 'increment',
});

/**
 * Optimistic updates for mutating a product's selection quantity.
 * @param {string} productID - The ID of the product/selection to mutate.
 * @returns {Object} - Functions for mutating a product's selection quantites.
 */
function useQuantityStepper({
    aisleID,
    carouselID,
    carouselName,
    carouselPosition,
    carouselType,
    cart,
    customAisle,
    dsSource,
    isCarouselModalCard = false,
    isGwp = false,
    isPerk = false,
    plusMemberPriceEligible,
    pointPrice,
    productID,
    productName,
    searchAutocomplete,
    searchRecommendation,
    upSellsSource = null,
}) {
    const {
        addCounter,
        alerts,
        searchQuery,
        searchResultsLength,
        setAddCounter,
        setAlerts,
        setGwpErrorToast,
        setProductAdded,
        setProductAddedID,
    } = useGlobalStore();

    const { emblaApi = {} } = useCarouselContext();

    const queryClient = useQueryClient();

    const { chargeID, plusMembershipEligibleOrder, productsData } = useProductCardContext();

    const source = useProductSource({
        aisleID,
        carouselID,
        carouselName,
        customAisle,
        searchAutocomplete,
        searchRecommendation,
        upSellsSource,
    });

    const plusMembershipPricing = plusMemberPriceEligible && plusMembershipEligibleOrder;

    const isLoading = !chargeID || productsData?.msg !== 'Success';

    const queryKey = [SELECTIONS_QUERY_KEY, chargeID];

    const productIsBundle = productsData?.bundles?.find((bundle) => bundle.bundleID === productID);

    // NOTE: the isPerk property identifies a perk selection, but when mutating, the BE expects asPerk instead.
    // Similarly, isGwp identifies a GWP selection, but when mutating, the BE expects asGwp instead.
    const sendParams = {};

    sendParams[productIsBundle ? 'sellableBundleID' : 'sellableItemID'] = productID;

    if (!cart) {
        sendParams.id = productID;
        sendParams.qty = 0;
        sendParams.aisleID = aisleID ?? null;
        sendParams.carouselID = carouselID ?? null;
        sendParams.searchTerm = window.location.pathname.includes('search') || searchAutocomplete ? searchQuery : '';
        sendParams.device = window.isNativeApp ? 'app' : 'web';
        sendParams.sourcePage = source;
        sendParams.asPerk = isPerk;
        sendParams.asGwp = isGwp;
        sendParams.dsSource = dsSource;
    }

    /**
     * Determines whether to update the `selected` or `selectedBundles` key.
     * @param {OperationTypes} operation - The operation to perform on the item's qty.
     * @param {Selection[]} selections - The relevant list of selections.
     */
    const getUpdatedSelections = ({ operation, selections }) => {
        const selectionsToUpdate = productIsBundle ? selections.selectedBundles : selections.selected;

        const { newQty, updatedSelections } = updateSelectionsWithOperation({
            cart,
            isGwp,
            isPerk,
            operation,
            pointPrice,
            productIsBundle,
            productID,
            productsData,
            selections: selectionsToUpdate,
        });

        if (newQty !== null) {
            sendParams.qty = newQty;
        }

        return {
            ...selections,
            ...(!productIsBundle && { selected: updatedSelections }),
            ...(productIsBundle && { selectedBundles: updatedSelections }),
        };
    };

    /**
     * Determines the appropriate API endpoint to call based on the passed in
     * operation and product type (item, bundle).
     * @param {OperationTypes} operation - The operation to perform on the item's qty.
     */
    const getMutationFn = ({ operation }) => {
        if (!productIsBundle && operation === OperationTypes.INCREMENT) {
            return incrementItemSelectionQty({ chargeID, sendParams });
        }
        if (!productIsBundle && operation === OperationTypes.DECREMENT) {
            return decrementItemSelectionQty({ chargeID, sendParams });
        }
        if (productIsBundle && operation === OperationTypes.INCREMENT) {
            return incrementBundleSelectionQty({ chargeID, sendParams });
        }
        if (productIsBundle && operation === OperationTypes.DECREMENT) {
            return decrementBundleSelectionQty({ chargeID, sendParams });
        }
        return null;
    };

    /**
     * Tracks an update quantity analytics event.
     *
     * @param {Object} location - A Location object.
     * @param {OperationTypes} operationType - The operation type.
     */
    const trackUpdateQtyEvent = ({ location, operationType }) => {
        const getEventTitle = () => {
            if (isGwp) return 'gwp v2 item quantity change';
            if (isPerk) return 'loyalty item quantity change';
            return 'item quantity change';
        };

        const { selected = [], selectedBundles = [] } = queryClient.getQueryData(queryKey);
        const isBundle = !!sendParams.sellableBundleID;
        const item = isBundle
            ? selectedBundles.find(({ id }) => id === sendParams.sellableBundleID)
            : selected.find(({ id }) => id === sendParams.sellableItemID);

        trackEvent(getEventTitle(), {
            type: operationType,
            aisle_id: aisleID ?? null,
            carousel_name: carouselName ?? '',
            carousel_position: carouselPosition ?? null,
            carousel_type: carouselType,
            chargeID,
            has_membership_pricing: plusMembershipPricing,
            horizontal_scroll: emblaApi?.selectedScrollSnap?.() ?? null,
            isCarouselModalCard,
            location,
            modal: isCarouselModalCard,
            newQuantity: item?.qty || 0,
            pathname: window.location.pathname,
            product_name: productName,
            productID,
            search_term: searchQuery,
            search_results_length: searchResultsLength,
        });
    };

    /**
     * TanStack Query hook to optimistically update the cache directly.
     * Reference: https://tanstack.com/query/v4/docs/react/guides/optimistic-updates
     */
    const productQtyMutation = useMutation({
        // API call:
        mutationFn: getMutationFn,

        // When mutate is called:
        onMutate: async (args) => {
            // Cancel any outgoing refetches, such that
            // they don't overwrite our optimistic update.
            await queryClient.cancelQueries({ queryKey });

            // Snapshot the previous selections.
            const previousSelections = queryClient.getQueryData(queryKey);

            // _Optimistically_ update the selections.
            queryClient.setQueryData(queryKey, (selections) =>
                getUpdatedSelections({
                    operation: args.operation,
                    selections,
                    sendParams,
                })
            );

            // Hide the curated cart banner upon any mutation to a product qty.
            const { hideCuratedCartBanner } = getOrderSessionStorage();
            if (!hideCuratedCartBanner) {
                setOrderSessionStorage({ hideCuratedCartBanner: true });
            }

            // Return a context object with the snapshotted previous selections.
            return { previousSelections };
        },

        // If the mutation fails:
        onError: (err, variables, context) => {
            // Use the context returned from onMutate to roll back.
            queryClient.setQueryData(queryKey, context.previousSelections);
            logError(err);
            console.error(err);

            // Show toast for GWP add failures
            if (isGwp && variables.operation === OperationTypes.INCREMENT) {
                setGwpErrorToast(true);
            }
        },

        // After success:
        onSuccess: (_, variables) => {
            const { location, operation } = variables;
            trackUpdateQtyEvent({ location, operationType: operation });
        },

        // After error or success:
        onSettled: (data, error, variables, context) => {
            // Refetch selections.
            queryClient.invalidateQueries({ queryKey });
            if (data?.msg === 'Max point spend exceeded') {
                setAlerts([...alerts, 'maxLoyaltyPointsSpendAlert']);
            }

            // Handle GWP add failures - check for non-success response
            // Only check when data exists; network errors (data undefined) are handled in onError
            if (data && data.msg !== 'Success' && isGwp && variables?.operation === OperationTypes.INCREMENT) {
                // Roll back optimistic update
                queryClient.setQueryData(queryKey, context?.previousSelections);
                setGwpErrorToast(true);
            }
        },
    });

    /**
     * Decrement the product's selection quantity.
     * Removes the selection if its mutated value is zero.
     *
     * @param {string} location - a string representing where in the UI the interaction took place (eg, "cart", or "product-card")
     *
     * @example decrementProductQty("cart");
     */
    const decrementProductQty = (location) =>
        productQtyMutation.mutate({
            chargeID,
            location,
            operation: OperationTypes.DECREMENT,
            sendParams,
        });

    /**
     * Increment the product's selection quantity.
     * Adds a new selection if one does not already exist.
     *
     * @param {string} location - a string representing where in the UI the interaction took place (eg, "cart", or "product-card")
     *
     * @example incrementProductQty("cart");
     */
    const incrementProductQty = (location) => {
        productQtyMutation.mutate({
            chargeID,
            location,
            operation: OperationTypes.INCREMENT,
            sendParams,
        });
        setAddCounter(addCounter + 1);
        setProductAdded(true);
        setProductAddedID(productID);
    };

    return { decrementProductQty, incrementProductQty, isLoading };
}

export default useQuantityStepper;
